import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  User, 
  LogOut, 
  Download, 
  FileText, 
  FileJson, 
  Bell,
  Settings,
  Building2,
  CreditCard,
  BookOpen,
  Shield,
  MessageSquarePlus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTransactions } from '../context/TransactionContext';
import { useSubscription } from '../context/SubscriptionContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YEARS = [2024, 2025, 2026, 2027, 2028];

const Header = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { selectedYear, setSelectedYear, exportCSV, exportJSON, urssafNotifications } = useTransactions();
  const { hasPaidPlan, createCustomerPortalSession } = useSubscription();
  const [notifOpen, setNotifOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/admin/is-admin`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [token]);

  // Compter les notifications importantes
  const importantNotifications = urssafNotifications?.filter(
    n => n.statut === 'en_retard' || n.statut === 'a_declarer'
  ) || [];

  const handleExportCSV = async () => {
    try {
      await exportCSV(selectedYear);
      toast.success('Export CSV réussi');
    } catch (error) {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  const handleExportJSON = async () => {
    try {
      await exportJSON(selectedYear);
      toast.success('Export JSON réussi');
    } catch (error) {
      toast.error('Erreur lors de l\'export JSON');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleSettings = () => {
    navigate('/app/parametres');
  };

  const handleMyBusiness = () => {
    toast.info('Page "Mon entreprise" à venir');
  };

  const handleBilling = async () => {
    // Si l'utilisateur a un plan payant, ouvrir le portail Stripe
    if (hasPaidPlan()) {
      setBillingLoading(true);
      try {
        const portalUrl = await createCustomerPortalSession();
        if (portalUrl) {
          window.location.href = portalUrl;
        } else {
          // Fallback vers la page pricing
          navigate('/app/pricing');
        }
      } catch (error) {
        console.error('Erreur portail:', error);
        navigate('/app/pricing');
      } finally {
        setBillingLoading(false);
      }
    } else {
      // Sinon, aller vers la page pricing
      navigate('/app/pricing');
    }
  };

  return (
    <header 
      className="h-16 bg-white border-b border-slate-200 shadow-sm flex items-center justify-between px-4 md:px-8 sticky top-0 z-30"
      data-testid="header"
    >
      {/* Left side - Logo */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="menu-btn"
        >
          <Menu className="h-6 w-6 text-slate-600" />
        </button>
        
        <div className="hidden md:flex items-center gap-2">
          <div className="p-1.5 bg-blue-600 rounded-lg">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-chivo font-bold text-xl text-slate-900 tracking-tight">
            5minCompta
          </h1>
        </div>
      </div>

      {/* Center - Year selector */}
      <div className="flex-1 flex justify-center">
        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-[110px] bg-slate-50 border-slate-200" data-testid="year-selector">
            <SelectValue placeholder="Année" />
          </SelectTrigger>
          <SelectContent>
            {YEARS.map(year => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Feedback Button */}
        <Button 
          variant="default" 
          size="sm" 
          className="bg-blue-600 hover:bg-blue-700 text-white hidden sm:flex"
          onClick={() => {
            // Dispatch custom event to open feedback modal
            window.dispatchEvent(new CustomEvent('openFeedback'));
          }}
          data-testid="header-feedback-btn"
        >
          <MessageSquarePlus className="h-4 w-4 mr-2" />
          Feedback utilisateur
        </Button>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="hidden sm:flex" data-testid="export-btn">
              <Download className="h-4 w-4 text-slate-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Exporter {selectedYear}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-btn">
              <FileText className="h-4 w-4 mr-2" />
              Export CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportJSON} data-testid="export-json-btn">
              <FileJson className="h-4 w-4 mr-2" />
              Export JSON
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              data-testid="notifications-btn"
            >
              <Bell className="h-4 w-4 text-slate-600" />
              {importantNotifications.length > 0 && (
                <Badge 
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
                >
                  {importantNotifications.length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {importantNotifications.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {importantNotifications.length} nouvelle{importantNotifications.length > 1 ? 's' : ''}
                </Badge>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {importantNotifications.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">
                Aucune notification
              </div>
            ) : (
              importantNotifications.slice(0, 3).map((notif) => (
                <DropdownMenuItem 
                  key={notif.id} 
                  className="flex flex-col items-start gap-1 py-3 cursor-pointer"
                  onClick={() => {
                    setNotifOpen(false);
                    navigate('/app');
                  }}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className={`w-2 h-2 rounded-full ${notif.statut === 'en_retard' ? 'bg-red-500' : 'bg-amber-500'}`} />
                    <span className="font-medium text-sm truncate flex-1">
                      {notif.statut === 'en_retard' ? '⚠️ En retard' : '📅 À déclarer'}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500 pl-4">
                    {notif.periode} - {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(notif.montant_ca)}
                  </span>
                </DropdownMenuItem>
              ))
            )}
            {importantNotifications.length > 3 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-center text-blue-600 justify-center"
                  onClick={() => {
                    setNotifOpen(false);
                    navigate('/app');
                  }}
                >
                  Voir toutes les notifications
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full bg-slate-100 hover:bg-slate-200" 
              data-testid="user-menu-btn"
            >
              <User className="h-4 w-4 text-slate-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="py-3">
              <div className="flex flex-col">
                <span className="font-semibold text-slate-900">{user?.name}</span>
                <span className="text-xs text-slate-500 font-normal mt-0.5">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isAdmin && (
              <>
                <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                  <Shield className="h-4 w-4 mr-3 text-purple-500" />
                  <span>Dashboard Admin</span>
                  <Badge className="ml-auto bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={handleSettings} data-testid="settings-menu-item">
              <Settings className="h-4 w-4 mr-3 text-slate-500" />
              <span>Paramètres</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleMyBusiness} data-testid="business-menu-item">
              <Building2 className="h-4 w-4 mr-3 text-slate-500" />
              <span>Mon entreprise</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleBilling} data-testid="billing-menu-item">
              <CreditCard className="h-4 w-4 mr-3 text-slate-500" />
              <span>Facturation</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout} 
              className="text-red-600 focus:text-red-600 focus:bg-red-50" 
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-3" />
              <span>Déconnexion</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
